"use client";

import type React from "react";

import { useFormContext } from "react-hook-form";
import { PlusCircle, Trash2 } from "lucide-react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n/hook";
import { Label } from "@/components/ui/label";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";

export function StepLifeGoals() {
  const { control, watch, setValue } = useFormContext();
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  const housingPurchasePlanned = watch("lifeGoals.housingPurchase.planned");
  const educationFunds = watch("lifeGoals.educationFunds") || [];
  const otherGoals = watch("lifeGoals.otherGoals") || [];

  const addEducationFund = () => {
    setValue("lifeGoals.educationFunds", [
      ...educationFunds,
      { name: "", age: 0, amount: 0 },
    ]);
  };

  const removeEducationFund = (index: number) => {
    setValue(
      "lifeGoals.educationFunds",
      educationFunds.filter((_: any, i: number) => i !== index)
    );
  };

  const addOtherGoal = () => {
    setValue("lifeGoals.otherGoals", [
      ...otherGoals,
      { name: "", year: 0, amount: 0 },
    ]);
  };

  const removeOtherGoal = (index: number) => {
    setValue(
      "lifeGoals.otherGoals",
      otherGoals.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t("lifePlan.lifeGoals.title")}</h2>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="housing">
          <AccordionTrigger>
            {t("lifePlan.lifeGoals.housing.title")}
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="housingPurchasePlanned"
                    checked={housingPurchasePlanned}
                    onCheckedChange={(checked) =>
                      setValue("lifeGoals.housingPurchase.planned", checked)
                    }
                  />
                  <Label htmlFor="housingPurchasePlanned">
                    {t("lifePlan.lifeGoals.housing.planned")}
                  </Label>
                </div>

                {housingPurchasePlanned && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <FormField
                      control={control}
                      name="lifeGoals.housingPurchase.year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("lifePlan.lifeGoals.housing.purchaseYear")}
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <FormControl>
                              <NumberInput
                                min={0}
                                max={100}
                                value={field.value}
                                onValueChange={(val) => field.onChange(val ?? 0)}
                                ref={field.ref}
                                name={field.name}
                                onBlur={field.onBlur}
                              />
                            </FormControl>
                            <span className="text-sm">{t("common.age")}</span>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="lifeGoals.housingPurchase.amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("lifePlan.lifeGoals.housing.purchaseAmount")}
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <FormControl>
                              <NumberInput
                                min={0}
                                value={field.value}
                                onValueChange={(val) => field.onChange(val ?? 0)}
                                ref={field.ref}
                                name={field.name}
                                onBlur={field.onBlur}
                              />
                            </FormControl>
                            <span className="text-sm">{currency}</span>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="education">
          <AccordionTrigger>
            {t("lifePlan.lifeGoals.education.title")}
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">
                    {t("lifePlan.lifeGoals.education.subtitle")}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addEducationFund}
                    className="h-8 px-2 text-xs"
                  >
                    <PlusCircle className="mr-1 h-3.5 w-3.5" />
                    {t("common.add")}
                  </Button>
                </div>

                {educationFunds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("lifePlan.lifeGoals.education.noData")}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {educationFunds.map((_: any, index: number) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 relative"
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeEducationFund(index)}
                          className="absolute top-2 right-2 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">{t("common.delete")}</span>
                        </Button>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={control}
                            name={`lifeGoals.educationFunds.${index}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("lifePlan.lifeGoals.education.name")}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder={t(
                                      "lifePlan.lifeGoals.education.namePlaceholder"
                                    )}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`lifeGoals.educationFunds.${index}.age`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("lifePlan.lifeGoals.education.childAge")}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      max={30}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">
                                    {t("common.age")}
                                  </span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`lifeGoals.educationFunds.${index}.amount`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "lifePlan.lifeGoals.education.requiredAmount"
                                  )}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">{currency}</span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="other-goals">
          <AccordionTrigger>
            {t("lifePlan.lifeGoals.other.title")}
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">
                    {t("lifePlan.lifeGoals.other.subtitle")}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOtherGoal}
                    className="h-8 px-2 text-xs"
                  >
                    <PlusCircle className="mr-1 h-3.5 w-3.5" />
                    {t("common.add")}
                  </Button>
                </div>

                {otherGoals.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("lifePlan.lifeGoals.other.noData")}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {otherGoals.map((_: any, index: number) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 relative"
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOtherGoal(index)}
                          className="absolute top-2 right-2 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">{t("common.delete")}</span>
                        </Button>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <FormField
                            control={control}
                            name={`lifeGoals.otherGoals.${index}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("lifePlan.lifeGoals.other.name")}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder={t(
                                      "lifePlan.lifeGoals.other.namePlaceholder"
                                    )}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`lifeGoals.otherGoals.${index}.year`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("lifePlan.lifeGoals.other.plannedAge")}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      max={100}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">
                                    {t("common.age")}
                                  </span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`lifeGoals.otherGoals.${index}.amount`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("lifePlan.lifeGoals.other.requiredAmount")}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">{currency}</span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="retirement">
          <AccordionTrigger>
            {t("lifePlan.lifeGoals.retirement.title")}
          </AccordionTrigger>
          <AccordionContent>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={control}
                    name="lifeGoals.retirementAge"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>
                            {t("lifePlan.lifeGoals.retirement.age")}
                          </FormLabel>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <NumberInput
                              min={50}
                              max={100}
                              value={field.value}
                              onValueChange={(val) => field.onChange(val ?? 0)}
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <span className="text-sm">{t("common.age")}</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="lifeGoals.retirementMonthlyExpense"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>
                            {t("lifePlan.lifeGoals.retirement.monthlyExpense")}
                          </FormLabel>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <NumberInput
                              min={0}
                              value={field.value}
                              onValueChange={(val) => field.onChange(val ?? 0)}
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <span className="text-sm">
                            {currency}
                            {t("common.perMonth")}
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
